import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Cpu, Thermometer, Waves } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1702254936198-58c34edae4b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxtYWxlJTIwYW5kcm9pZCUyMGN5Ym9yZyUyMHNjdWxwdHVyZSUyMGRhcmslMjBmdXR1cmlzdGljfGVufDB8fHx8MTc4OTQxNTkwMXww&ixlib=rb-4.1.0&q=85";

const lines = [
  { text: "SYNTHETIC", cls: "text-white" },
  { text: "DESIRE,", cls: "text-magma" },
  { text: "ENGINEERED.", cls: "text-outline" },
];

const chips = [
  { icon: Cpu, label: "AURA-9 NEURAL CORE" },
  { icon: Thermometer, label: "TACTILE 37.2°C" },
  { icon: Waves, label: "MICRO-EXPRESSION ENGINE" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 5]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);

  const scrollToCatalog = () => {
    const el = document.getElementById("catalog");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden pt-16"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,42,109,0.14),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(5,217,232,0.08),transparent_55%)]" />

      <div className="relative z-10 w-full px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 py-24">
          <motion.p
            className="font-mono-tech text-xs uppercase tracking-[0.35em] text-cyber mb-8"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Aetheria Companion Systems // Est. 2026
          </motion.p>

          <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{
                    delay: 0.45 + i * 0.16,
                    duration: 1,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="mt-8 max-w-md text-base text-slate-400 leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
          >
            Biomechanical male companions with adaptive-warmth skin, empathic
            neural cores and absolute privacy architecture. Built for adults.
            Shipped in silence.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-5"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.8 }}
          >
            <button
              data-testid="hero-explore-btn"
              onClick={scrollToCatalog}
              className="bg-magma text-white font-display font-bold uppercase tracking-widest text-sm px-8 py-4 glow-magenta hover:bg-[#ff4d87] transition-colors duration-300"
            >
              Explore the Collection
            </button>
            <a
              data-testid="hero-manifesto-link"
              href="#manifesto"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("manifesto")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="font-mono-tech text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-cyber transition-colors duration-300 border-b border-white/20 pb-1"
            >
              Read the Manifesto
            </a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-8 bg-[radial-gradient(circle_at_center,rgba(255,42,109,0.22),transparent_65%)] blur-2xl" />
            <motion.div
              style={{ y: imgY, rotate: imgRotate }}
              className="relative clip-frame overflow-hidden border border-white/10"
            >
              <img
                src={HERO_IMG}
                alt="Aetheria male companion android"
                className="w-full h-[70vh] object-cover"
              />
              <div className="scanlines" />
              <div className="absolute inset-0 bg-gradient-to-t from-void/80 via-transparent to-transparent" />
            </motion.div>
            {chips.map((chip, i) => (
              <motion.div
                key={chip.label}
                className="absolute flex items-center gap-2 bg-black/70 backdrop-blur-md border border-white/10 px-4 py-2.5 animate-float-slow"
                style={{
                  top: `${12 + i * 30}%`,
                  [i % 2 === 0 ? "left" : "right"]: "-1.5rem",
                  animationDelay: `${i * 1.4}s`,
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.6 + i * 0.2, duration: 0.6 }}
              >
                <chip.icon size={13} className="text-cyber" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-300">
                  {chip.label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={scrollToCatalog}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-cyber transition-colors"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
      >
        <ArrowDown size={20} />
      </motion.button>
    </section>
  );
}
